import JSZip from 'jszip';

/**
 * Interface for download target information
 */
export interface DownloadTarget {
  id: string;
  url: string;
  filename: string;
}

/**
 * Result of zip preparation including the zip blob and success/failure information
 */
export interface ZipResult {
  blob: Blob;
  successCount: number;
  failedCount: number;
  failedItems: DownloadTarget[];
}

/**
 * Fetches a file as ArrayBuffer from a URL with error handling
 * @param url - URL to fetch
 * @returns Promise resolving to ArrayBuffer or null if fetch failed
 */
const fetchFileAsArrayBuffer = async (url: string): Promise<ArrayBuffer | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.error(`Failed to fetch file: ${url}, status: ${response.status}`);
      return null;
    }
    return await response.arrayBuffer();
  } catch (error) {
    console.error(`Error fetching file: ${url}`, error);
    return null;
  }
};

/**
 * Creates a zip file containing the provided files
 * @param targets - Array of download targets with id, url, and filename
 * @returns Promise resolving to a ZipResult with the zip blob and success/failure counts
 */
export const prepareZip = async (targets: DownloadTarget[]): Promise<ZipResult> => {
  if (!targets || targets.length === 0) {
    throw new Error('No targets provided for zip preparation');
  }

  const zip = new JSZip();
  const successfulTargets: DownloadTarget[] = [];
  const failedTargets: DownloadTarget[] = [];

  // Process each target in parallel
  const fetchPromises = targets.map(async (target) => {
    const data = await fetchFileAsArrayBuffer(target.url);
    if (data) {
      zip.file(target.filename, data);
      successfulTargets.push(target);
    } else {
      failedTargets.push(target);
    }
  });

  // Wait for all fetch operations to complete
  await Promise.all(fetchPromises);

  // Generate the zip file
  const blob = await zip.generateAsync({ type: 'blob' });

  return {
    blob,
    successCount: successfulTargets.length,
    failedCount: failedTargets.length,
    failedItems: failedTargets
  };
};

/**
 * Triggers a file download for the provided blob
 * @param blob - The blob to download
 * @param filename - The filename to use for the download
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  // Create a URL for the blob
  const url = URL.createObjectURL(blob);
  
  // Create a temporary link element
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  
  // Append link to body, click it, and remove it
  document.body.appendChild(link);
  link.click();
  
  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};